import { Anchor } from "@mantine/core";
import { IconFileText, type TablerIcon } from "@tabler/icons-react";
import React, { type MouseEvent } from "react";
import { AnchorLink } from "../../../../components/AnchorLink/AnchorLink";
import { useDownload } from "../../../../hooks/useDownload";
import { useToast } from "../../../notifications/useToast";
import { MantineIconCell } from "./MantineIconCell";

type MantineLinkCellProps = {
  download?: boolean;
  IconComponent?: TablerIcon;
  renderedCellValue: React.ReactNode;
} & (
  | {
      url: string;
    }
  | {
      onClick: (e: MouseEvent<HTMLButtonElement>) => void;
    }
);

function MantineLinkCell({
  download = false,
  IconComponent = IconFileText,
  renderedCellValue,
  ...linkProps
}: MantineLinkCellProps) {
  return (
    <MantineIconCell
      IconComponent={IconComponent}
      renderedCellValue={
        "onClick" in linkProps ? (
          <DownloadButton
            renderedCellValue={renderedCellValue}
            onClick={linkProps.onClick}
          />
        ) : download ? (
          <DownloadLink
            renderedCellValue={renderedCellValue}
            url={linkProps.url}
          />
        ) : (
          <AnchorLink href={linkProps.url}>{renderedCellValue}</AnchorLink>
        )
      }
    />
  );
}

function DownloadLink({
  renderedCellValue,
  url: downloadUrl
}: Pick<MantineLinkCellProps, "renderedCellValue"> & {
  url: string;
}) {
  const { showErrorToast } = useToast();
  const { download, error } = useDownload();

  if (error) {
    showErrorToast(error);
  }

  function onClick(e: MouseEvent<HTMLButtonElement>) {
    download({ downloadUrl });
    e.preventDefault();
  }

  return (
    <DownloadButton renderedCellValue={renderedCellValue} onClick={onClick} />
  );
}

function DownloadButton({
  renderedCellValue,
  onClick
}: Pick<MantineLinkCellProps, "renderedCellValue"> & {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <Anchor
      component="button"
      inherit
      type="button"
      variant="external"
      onClick={onClick}
    >
      {renderedCellValue}
    </Anchor>
  );
}

export { MantineLinkCell, MantineLinkCellProps };
